import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Chip,
  InputAdornment,
  CircularProgress,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Avatar,
  Divider,
  Tooltip,
  Badge,
  
} from '@mui/material';
import {
  Search,
  Refresh,
  Close,
  CheckCircle,
  Cancel,
  AccountBalance,
  AttachMoney,
  Visibility,
  MonetizationOn,
  PersonOutline,
  Store,
  Payment,
  Info,
  CreditCard,
  Edit,
  HelpOutline
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { green, red, orange, blue, grey } from '@mui/material/colors';
import pb from './pocketbase';

// Styled components
const StatusChip = styled(Chip)(({ status, theme }) => ({
  fontWeight: 600,
  ...(status === 'pending' && {
    backgroundColor: orange[100],
    color: orange[800],
  }),
  ...(status === 'processing' && {
    backgroundColor: blue[100],
    color: blue[800],
  }),
  ...(status === 'paid' && {
    backgroundColor: green[100],
    color: green[800],
  }),
  ...(status === 'unpaid' && {
    backgroundColor: red[100],
    color: red[800],
  }),
  ...(status === 'rejected' && {
    backgroundColor: red[100],
    color: red[800],
  }),
}));

const StatCard = styled(Card)(({ theme }) => ({
  height: '100%',
  backgroundColor: theme.palette.background.paper,
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4],
  },
}));

const PaymentsManagement = () => {
  // Current date and user
  const currentDateTime = "2025-04-03 08:02:53";
  const currentUser = "MdAbuNafisNiloy";

  // State for tab management
  const [tabValue, setTabValue] = useState(0);

  // States for withdrawal requests
  const [withdrawals, setWithdrawals] = useState([]);
  const [withdrawalPage, setWithdrawalPage] = useState(0);
  const [withdrawalRowsPerPage, setWithdrawalRowsPerPage] = useState(10);
  const [totalWithdrawals, setTotalWithdrawals] = useState(0);
  const [withdrawalSearchQuery, setWithdrawalSearchQuery] = useState('');
  const [withdrawalFilterStatus, setWithdrawalFilterStatus] = useState('all');
  
  // States for commissions
  const [commissions, setCommissions] = useState([]);
  const [commissionPage, setCommissionPage] = useState(0);
  const [commissionRowsPerPage, setCommissionRowsPerPage] = useState(10);
  const [totalCommissions, setTotalCommissions] = useState(0);
  const [commissionSearchQuery, setCommissionSearchQuery] = useState('');
  
  // States for view and update dialogs
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [viewWithdrawalDialog, setViewWithdrawalDialog] = useState(false);
  const [updateStatusDialog, setUpdateStatusDialog] = useState(false);
  const [withdrawalStatus, setWithdrawalStatus] = useState('');
  const [viewSellerDialog, setViewSellerDialog] = useState(false);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [confirmDeductDialog, setConfirmDeductDialog] = useState(false);
  
  // States for loading and messages
  const [loadingWithdrawals, setLoadingWithdrawals] = useState(true);
  const [loadingCommissions, setLoadingCommissions] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // States for statistics
  const [statistics, setStatistics] = useState({
    totalWithdrawals: 0,
    pendingWithdrawals: 0,
    completedWithdrawals: 0,
    totalCommissions: 0,
    pendingAmount: 0,
    paidAmount: 0
  });

  // Function to fetch withdrawal requests
  const fetchWithdrawals = useCallback(async () => {
    try {
      setLoadingWithdrawals(true);
      
      // Build filter for withdrawals
      let filter = '';
      if (withdrawalSearchQuery) {
        filter += `seller_id~"${withdrawalSearchQuery}" || account_number~"${withdrawalSearchQuery}" || method~"${withdrawalSearchQuery}"`;
      }
      
      if (withdrawalFilterStatus !== 'all') {
        if (filter) filter += ' && ';
        filter += `status="${withdrawalFilterStatus}"`;
      }
      
      // Fetch withdrawals with pagination
      const result = await pb.collection('withdraw').getList(withdrawalPage + 1, withdrawalRowsPerPage, {
        filter,
        sort: '-created'
        // Removed expand: 'seller_id' since it's not working
      });
      
      // Get all unique seller IDs from the withdrawals
      const sellerIds = [...new Set(
        result.items
          .filter(withdrawal => withdrawal.seller_id)
          .map(withdrawal => withdrawal.seller_id)
      )];
      
      // If we have any seller IDs, fetch them in a single batch request
      let sellerData = {};
      if (sellerIds.length > 0) {
        try {
          // Create a filter to get all sellers in one request
          const sellerFilter = sellerIds.map(id => `id="${id}"`).join(' || ');
          
          // Fetch all sellers at once
          const sellers = await pb.collection('sellers').getList(1, 100, {
            filter: sellerFilter
          });
          
          // Create a lookup map for easy access
          sellers.items.forEach(seller => {
            sellerData[seller.id] = seller;
          });
          
          console.log(`Successfully fetched ${sellers.items.length} sellers for ${sellerIds.length} unique IDs`);
        } catch (err) {
          console.error('Error fetching sellers:', err);
        }
      }
      
      // Now manually add the expanded seller data to each withdrawal
      const expandedWithdrawals = result.items.map(withdrawal => {
        // Create a clone of the withdrawal to avoid modifying the original
        const expandedWithdrawal = { ...withdrawal };
        
        // Initialize expand object if it doesn't exist
        if (!expandedWithdrawal.expand) {
          expandedWithdrawal.expand = {};
        }
        
        // If we have seller data for this withdrawal, add it to the expand object
        if (withdrawal.seller_id && sellerData[withdrawal.seller_id]) {
          expandedWithdrawal.expand.seller_id = sellerData[withdrawal.seller_id];
        }
        
        return expandedWithdrawal;
      });
    
      setWithdrawals(expandedWithdrawals);
      setTotalWithdrawals(result.totalItems);
      
      // Fetch withdrawal statistics
      const stats = await fetchWithdrawalStats();
      setStatistics(prev => ({ ...prev, ...stats }));
      
      setLoadingWithdrawals(false);
    } catch (error) {
      console.error('Error fetching withdrawals:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load withdrawal requests',
        severity: 'error'
      });
      setLoadingWithdrawals(false);
    }
  }, [withdrawalPage, withdrawalRowsPerPage, withdrawalSearchQuery, withdrawalFilterStatus]);

  // Function to fetch user commissions
  const fetchCommissions = useCallback(async () => {
    try {
      setLoadingCommissions(true);
      
      // Fetch sellers who have commissions
      const sellerFilter = commissionSearchQuery 
        ? `name~"${commissionSearchQuery}" || email~"${commissionSearchQuery}" || shop_name~"${commissionSearchQuery}"`
        : '';
      
      const result = await pb.collection('sellers').getList(commissionPage + 1, commissionRowsPerPage, {
        filter: sellerFilter,
        sort: '-total_commission',
      });
      
      setCommissions(result.items);
      setTotalCommissions(result.totalItems);
      
      // Calculate commission statistics
      const totalCommissions = result.items.reduce((sum, seller) => sum + (seller.total_commission || 0), 0);
      
      setStatistics(prev => ({
        ...prev,
        totalCommissions
      }));
      
      setLoadingCommissions(false);
    } catch (error) {
      console.error('Error fetching commissions:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load commission data',
        severity: 'error'
      });
      setLoadingCommissions(false);
    }
  }, [commissionPage, commissionRowsPerPage, commissionSearchQuery]);

  // Function to fetch withdrawal statistics
  const fetchWithdrawalStats = async () => {
    try {
      // Total counts
      const pendingWithdrawals = await pb.collection('withdraw').getList(1, 1, {
        filter: 'status="pending"'
      });
      
      const completedWithdrawals = await pb.collection('withdraw').getList(1, 1, {
        filter: 'status="paid"'
      });
      
      // Total amounts
      const pendingAmount = await pb.collection('withdraw').getFullList({
        filter: 'status="pending"',
        fields: 'amount'
      });
      
      const paidAmount = await pb.collection('withdraw').getFullList({
        filter: 'status="paid"',
        fields: 'amount'
      });
      
      return {
        totalWithdrawals: pendingWithdrawals.totalItems + completedWithdrawals.totalItems,
        pendingWithdrawals: pendingWithdrawals.totalItems,
        completedWithdrawals: completedWithdrawals.totalItems,
        pendingAmount: pendingAmount.reduce((sum, item) => sum + (item.amount || 0), 0),
        paidAmount: paidAmount.reduce((sum, item) => sum + (item.amount || 0), 0)
      };
    } catch (error) {
      console.error('Error fetching withdrawal stats:', error);
      return {
        totalWithdrawals: 0,
        pendingWithdrawals: 0,
        completedWithdrawals: 0,
        pendingAmount: 0,
        paidAmount: 0
      };
    }
  };

  // Fetch data on initial load and when dependencies change
  useEffect(() => {
    if (tabValue === 0) {
      fetchWithdrawals();
    } else {
      fetchCommissions();
    }
  }, [tabValue, fetchWithdrawals, fetchCommissions]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'BDT',
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle withdrawal status update
  const updateWithdrawalStatus = async () => {
    if (!selectedWithdrawal) return;
    
    try {
      setIsSubmitting(true);
      
      // Update withdrawal status
      await pb.collection('withdraw').update(selectedWithdrawal.id, {
        status: withdrawalStatus
      });
      
      // If marking as paid, also update seller balance
    //   if (withdrawalStatus === 'paid' && selectedWithdrawal.seller_id) {
    //     // Get the seller record
    //     try {
    //       const seller = await pb.collection('sellers').getOne(selectedWithdrawal.seller_id);
          
    //       // Calculate new balance by subtracting withdrawal amount
    //       const newBalance = Math.max(0, (seller.total_balance || 0) - (selectedWithdrawal.amount || 0));
          
    //       // Update seller balance
    //       await pb.collection('sellers').update(seller.id, {
    //         total_balance: newBalance
    //       });
    //     } catch (error) {
    //       console.error('Error updating seller balance:', error);
    //       throw new Error('Failed to update seller balance');
    //     }
    //   }
      
      setSnackbar({
        open: true,
        message: `Withdrawal request marked as ${withdrawalStatus}`,
        severity: 'success'
      });
      
      fetchWithdrawals();
      setUpdateStatusDialog(false);
      setIsSubmitting(false);
    } catch (error) {
      console.error('Error updating withdrawal status:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update withdrawal status',
        severity: 'error'
      });
      setIsSubmitting(false);
    }
  };

  // Handle commission deduction
  const handleDeductCommission = async () => {
    if (!selectedSeller) return;
    
    try {
      setIsSubmitting(true);
      
      // Reset commission to zero
      await pb.collection('sellers').update(selectedSeller.id, {
        total_commission: 0
      });
      
      setSnackbar({
        open: true,
        message: `Commission deducted successfully for ${selectedSeller.shop_name || selectedSeller.name}`,
        severity: 'success'
      });
      
      fetchCommissions();
      setConfirmDeductDialog(false);
      setViewSellerDialog(false);
      setIsSubmitting(false);
    } catch (error) {
      console.error('Error deducting commission:', error);
      setSnackbar({
        open: true,
        message: 'Failed to deduct commission',
        severity: 'error'
      });
      setIsSubmitting(false);
    }
  };

  // View withdrawal details
  const handleViewWithdrawal = (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setViewWithdrawalDialog(true);
  };

  // Open update status dialog
  const handleUpdateStatus = (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setWithdrawalStatus(withdrawal.status);
    setUpdateStatusDialog(true);
  };

  // View seller details
  const handleViewSeller = async (sellerId) => {
    try {
      const seller = await pb.collection('sellers').getOne(sellerId);
      setSelectedSeller(seller);
      setViewSellerDialog(true);
    } catch (error) {
      console.error('Error fetching seller details:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load seller details',
        severity: 'error'
      });
    }
  };

  // Open confirm deduct dialog
  const handleConfirmDeduct = (seller) => {
    setSelectedSeller(seller);
    setConfirmDeductDialog(true);
  };

  // Render Statistics Cards
  const renderStatisticsCards = () => {
    if (tabValue === 0) {
      return (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: blue[500], mr: 2 }}>
                    <AccountBalance />
                  </Avatar>
                  <Typography variant="h6" component="div">
                    Total Withdrawals
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight={600}>
                  {statistics.totalWithdrawals}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total amount: {formatCurrency(statistics.pendingAmount + statistics.paidAmount)}
                </Typography>
              </CardContent>
            </StatCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: orange[500], mr: 2 }}>
                    <Payment />
                  </Avatar>
                  <Typography variant="h6" component="div">
                    Pending
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight={600}>
                  {statistics.pendingWithdrawals}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pending amount: {formatCurrency(statistics.pendingAmount)}
                </Typography>
              </CardContent>
            </StatCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: green[500], mr: 2 }}>
                    <CheckCircle />
                  </Avatar>
                  <Typography variant="h6" component="div">
                    Completed
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight={600}>
                  {statistics.completedWithdrawals}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Paid amount: {formatCurrency(statistics.paidAmount)}
                </Typography>
              </CardContent>
            </StatCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: red[500], mr: 2 }}>
                    <MonetizationOn />
                  </Avatar>
                  <Typography variant="h6" component="div">
                    Commission
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight={600}>
                  {formatCurrency(statistics.totalCommissions)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total charged from withdrawals
                </Typography>
              </CardContent>
            </StatCard>
          </Grid>
        </Grid>
      );
    } else {
      return (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6}>
            <StatCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: blue[500], mr: 2 }}>
                    <Store />
                  </Avatar>
                  <Typography variant="h6" component="div">
                    Total Sellers
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight={600}>
                  {totalCommissions}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sellers with commission records
                </Typography>
              </CardContent>
            </StatCard>
          </Grid>
          <Grid item xs={12} sm={6}>
            <StatCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: green[500], mr: 2 }}>
                    <MonetizationOn />
                  </Avatar>
                  <Typography variant="h6" component="div">
                    Total Commission
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight={600}>
                  {formatCurrency(statistics.totalCommissions)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pending commission amount to collect
                </Typography>
              </CardContent>
            </StatCard>
          </Grid>
        </Grid>
      );
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Payments Management
        </Typography>
        <Typography variant="body1" color="text.secondary" gutterBottom>
          Manage withdrawal requests and seller commissions
        </Typography>
        
        {/* Tabs */}
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            '& .MuiTab-root': { 
              textTransform: 'none',
              fontWeight: 600,
              py: 2
            }
          }}
        >
          <Tab 
            icon={<AccountBalance fontSize="small" />} 
            iconPosition="start" 
            label="Withdrawal Requests"
          />
          <Tab 
            icon={<MonetizationOn fontSize="small" />} 
            iconPosition="start" 
            label="Seller Commissions" 
          />
        </Tabs>
      </Box>

      {/* Statistics */}
      {renderStatisticsCards()}

      {/* Withdrawals Tab Content */}
      {tabValue === 0 && (
        <>
          {/* Search and filters */}
          <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Search by seller, payment method, or account number..."
                  value={withdrawalSearchQuery}
                  onChange={(e) => {
                    setWithdrawalSearchQuery(e.target.value);
                    setWithdrawalPage(0);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search color="action" />
                      </InputAdornment>
                    ),
                  }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={withdrawalFilterStatus}
                      onChange={(e) => {
                        setWithdrawalFilterStatus(e.target.value);
                        setWithdrawalPage(0);
                      }}
                      label="Status"
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="processing">Processing</MenuItem>
                      <MenuItem value="paid">Paid</MenuItem>
                      <MenuItem value="unpaid">Unpaid</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={() => {
                      setWithdrawalSearchQuery('');
                      setWithdrawalFilterStatus('all');
                      setWithdrawalPage(0);
                      fetchWithdrawals();
                    }}
                    size="small"
                  >
                    Reset
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Withdrawals Table */}
          <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 2, mb: 4 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Seller</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Payment Method</TableCell>
                    <TableCell>Account Number</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingWithdrawals ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                        <CircularProgress size={40} />
                        <Typography variant="body2" sx={{ mt: 2 }}>
                          Loading withdrawal requests...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : withdrawals.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                        <Typography variant="body1">
                          No withdrawal requests found
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {withdrawalFilterStatus !== 'all' || withdrawalSearchQuery
                            ? 'Try adjusting your search or filters'
                            : 'There are no withdrawal requests to manage yet'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    withdrawals.map((withdrawal) => (
                      <TableRow key={withdrawal.id} hover>
                        <TableCell>
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              cursor: 'pointer' 
                            }}
                            onClick={() => handleViewSeller(withdrawal.seller_id)}
                          >
                            <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: blue[500] }}>
                              {withdrawal.expand?.seller_id?.name?.charAt(0) || 'S'}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={500}>
                                {withdrawal.expand?.seller_id?.shop_name || 'Unknown Shop'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {withdrawal.expand?.seller_id?.full_name || 'Unknown Seller'}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {formatDate(withdrawal.created)}
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={600}>
                            {formatCurrency(withdrawal.amount)}
                          </Typography>
                          {withdrawal.commission_charged > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              Commission: {formatCurrency(withdrawal.commission_charged)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {withdrawal.method}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={withdrawal.account_number}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                              {withdrawal.account_number}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <StatusChip
                            label={withdrawal.status}
                            status={withdrawal.status}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Tooltip title="View Details">
                              <IconButton 
                                size="small" 
                                onClick={() => handleViewWithdrawal(withdrawal)}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            
                            <Tooltip title="Edit Status">
                              <IconButton 
                                size="small" 
                                color="primary"
                                onClick={() => handleUpdateStatus(withdrawal)}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalWithdrawals}
              rowsPerPage={withdrawalRowsPerPage}
              page={withdrawalPage}
              onPageChange={(e, newPage) => setWithdrawalPage(newPage)}
              onRowsPerPageChange={(e) => {
                setWithdrawalRowsPerPage(parseInt(e.target.value, 10));
                setWithdrawalPage(0);
              }}
            />
          </Paper>
        </>
      )}

      {/* Commissions Tab Content */}
      {tabValue === 1 && (
        <>
          {/* Search */}
          <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Search by seller name or shop name..."
                  value={commissionSearchQuery}
                  onChange={(e) => {
                    setCommissionSearchQuery(e.target.value);
                    setCommissionPage(0);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search color="action" />
                      </InputAdornment>
                    ),
                  }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={() => {
                      setCommissionSearchQuery('');
                      setCommissionPage(0);
                      fetchCommissions();
                    }}
                    size="small"
                  >
                    Refresh
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Commissions Table */}
          <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 2 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Seller</TableCell>
                    <TableCell>Shop Name</TableCell>
                    <TableCell align="right">Total Sales</TableCell>
                    <TableCell align="right">Commission</TableCell>
                    <TableCell align="right">Balance</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingCommissions ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                        <CircularProgress size={40} />
                        <Typography variant="body2" sx={{ mt: 2 }}>
                          Loading seller commissions...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : commissions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                        <Typography variant="body1">
                          No seller commissions found
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {commissionSearchQuery
                            ? 'Try adjusting your search'
                            : 'There are no seller commissions to manage yet'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    commissions.map((seller) => (
                      <TableRow key={seller.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: blue[500] }}>
                              {seller.name?.charAt(0) || 'S'}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={500}>
                                {seller.full_name || 'Unknown'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {seller.email || 'No email'}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {seller.shop_name || 'N/A'}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(seller.total_sale || 0)}
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={600} color={seller.total_commission > 0 ? 'success.main' : 'text.primary'}>
                            {formatCurrency(seller.total_commission || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(seller.total_balance || 0)}
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Tooltip title="View Seller Details">
                              <IconButton 
                                size="small" 
                                onClick={() => handleViewSeller(seller.id)}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {seller.total_commission > 0 && (
                              <Tooltip title="Deduct Commission">
                                <IconButton 
                                  size="small" 
                                  color="success"
                                  onClick={() => handleConfirmDeduct(seller)}
                                >
                                  <MonetizationOn fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalCommissions}
              rowsPerPage={commissionRowsPerPage}
              page={commissionPage}
              onPageChange={(e, newPage) => setCommissionPage(newPage)}
              onRowsPerPageChange={(e) => {
                setCommissionRowsPerPage(parseInt(e.target.value, 10));
                setCommissionPage(0);
              }}
            />
          </Paper>
        </>
      )}

      {/* View Withdrawal Dialog */}
      <Dialog 
        open={viewWithdrawalDialog} 
        onClose={() => setViewWithdrawalDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        {selectedWithdrawal && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  Withdrawal Request Details
                </Typography>
                <IconButton onClick={() => setViewWithdrawalDialog(false)}>
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Withdrawal Status
                  </Typography>
                  <StatusChip
                    label={selectedWithdrawal.status}
                    status={selectedWithdrawal.status}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Seller
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedWithdrawal.expand?.seller_id?.full_name || 'Unknown'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Shop Name
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedWithdrawal.expand?.seller_id?.shop_name || 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Request Date
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {formatDate(selectedWithdrawal.created)}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Last Updated
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {formatDate(selectedWithdrawal.updated)}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Amount
                  </Typography>
                  <Typography variant="h6" fontWeight={600} color="primary">
                    {formatCurrency(selectedWithdrawal.amount)}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Commission Charged
                  </Typography>
                  <Typography variant="body1" fontWeight={500} color="secondary">
                    {formatCurrency(selectedWithdrawal.commission_charged)}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Payment Method
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedWithdrawal.method}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Account Number
                  </Typography>
                  <Typography variant="body1" fontWeight={500} sx={{ wordBreak: 'break-all' }}>
                    {selectedWithdrawal.account_number}
                  </Typography>
                </Grid>
              </Grid>
              
              {selectedWithdrawal.status === 'pending' && (
                <Box sx={{ mt: 3 }}>
                  <Alert severity="info" icon={<Info />}>
                    This withdrawal request is awaiting your approval. You can mark it as paid once the payment is processed.
                  </Alert>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setViewWithdrawalDialog(false)}
                variant="outlined"
              >
                Close
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setViewWithdrawalDialog(false);
                  handleUpdateStatus(selectedWithdrawal);
                }}
              >
                Edit Status
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog 
        open={updateStatusDialog} 
        onClose={() => !isSubmitting && setUpdateStatusDialog(false)}
      >
        <DialogTitle>
          Update Withdrawal Status
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {selectedWithdrawal && (
              <>
                <Typography variant="subtitle1" gutterBottom>
                  Please select withdrawal status:
                </Typography>
                
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={withdrawalStatus}
                    onChange={(e) => setWithdrawalStatus(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="paid">Paid</MenuItem>
                    <MenuItem value="unpaid">Unpaid</MenuItem>
                  </Select>
                </FormControl>
                
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" gutterBottom>
                    <strong>Amount:</strong> {formatCurrency(selectedWithdrawal.amount)}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Seller:</strong> {selectedWithdrawal.expand?.seller_id?.full_name || 'Unknown'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Payment Method:</strong> {selectedWithdrawal.method}
                  </Typography>
                </Box>
                
                {withdrawalStatus === 'paid' && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Marking as "Paid" will update the seller's balance.
                  </Alert>
                )}
                
                {withdrawalStatus === 'unpaid' && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    Marking as "Unpaid" will keep the amount in the seller's balance.
                  </Alert>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setUpdateStatusDialog(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={updateWithdrawalStatus}
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            {isSubmitting ? 'Updating...' : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Seller Dialog */}
      <Dialog 
        open={viewSellerDialog} 
        onClose={() => setViewSellerDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        {selectedSeller && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  Seller Details
                </Typography>
                <IconButton onClick={() => setViewSellerDialog(false)}>
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar sx={{ width: 64, height: 64, mr: 2, bgcolor: blue[500] }}>
                  {selectedSeller.name?.charAt(0) || 'S'}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={500}>
                    {selectedSeller.full_name || 'Unknown Seller'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedSeller.email || 'No email available'}
                  </Typography>
                </Box>
              </Box>
              
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Shop Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Shop Name
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedSeller.shop_name || 'No shop name'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Phone
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedSeller.phone || 'No phone number'}
                  </Typography>
                </Grid>
              </Grid>
              
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Financial Information
              </Typography>
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Total Sales
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        {formatCurrency(selectedSeller.total_sale || 0)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Total Commission
                      </Typography>
                      <Typography variant="h6" fontWeight={600} color="secondary.main">
                        {formatCurrency(selectedSeller.total_commission || 0)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Current Balance
                      </Typography>
                      <Typography variant="h6" fontWeight={600} color="primary.main">
                        {formatCurrency(selectedSeller.total_balance || 0)}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              
              {selectedSeller.total_commission > 0 && (
                <Alert 
                  severity="info" 
                  action={
                    <Button 
                      color="info" 
                      size="small"
                      onClick={() => handleConfirmDeduct(selectedSeller)}
                    >
                      Deduct Now
                    </Button>
                  }
                >
                  This seller has {formatCurrency(selectedSeller.total_commission)} in pending commissions that can be deducted.
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setViewSellerDialog(false)}
                variant="outlined"
              >
                Close
              </Button>
              {selectedSeller.total_commission > 0 && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<MonetizationOn />}
                  onClick={() => handleConfirmDeduct(selectedSeller)}
                >
                  Deduct Commission
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Confirm Deduct Commission Dialog */}
      <Dialog 
        open={confirmDeductDialog} 
        onClose={() => !isSubmitting && setConfirmDeductDialog(false)}
      >
        <DialogTitle>
          Confirm Commission Deduction
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Are you sure you want to deduct the commission from this seller? This action will reset their commission balance to zero.
          </Alert>
          
          {selectedSeller && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Seller:</strong> {selectedSeller.full_name || 'Unknown'}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Shop:</strong> {selectedSeller.shop_name || 'N/A'}
              </Typography>
              <Typography variant="body2" gutterBottom fontWeight={600}>
                <strong>Commission Amount:</strong> {formatCurrency(selectedSeller.total_commission || 0)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDeductDialog(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleDeductCommission}
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            {isSubmitting ? 'Processing...' : 'Confirm Deduction'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PaymentsManagement;